import React, { useMemo } from 'react';
import {
  SubmissionScoreEdit,
  ISubmissionScoreEditAssignment, ClassNames,
} from '@/components/shared/Grading/Navigation/SubmissionScoreEdit';
import { ISubmission } from '@/components/interfaces';
import useGradingStore from '../useGradingStore';

interface SubmissionScoreProps {
  classNames?: ClassNames;
  editable?: boolean;
  labelText?: string;
  onChange?: () => any;
}

export const useSubmissionScore = () => {
  const isAssessment = useGradingStore(state => state.isAssessment);
  const submission = useGradingStore(state => state.submission);
  const questionAnswers = useGradingStore(state => state.questionAnswers);

  // AIDEV-NOTE: PLAT-18100 — null means ungraded all the way down, so every fallback here is `??`
  // rather than `||`: a teacher-entered 0 override is a real score, and an assessment with nothing
  // graded yet has no running total to show. A 0 returned here renders as a scored zero.
  return useMemo(() => {
    if (!submission) return null;
    if (!isAssessment()) return submission.total_score;

    // For assessments, scores aren't calculated until all answers are graded.
    // So for the score, we will display a running total of what the score will be after everything is graded
    // or the actual values persisted to the DB.
    const scored = (questionAnswers || []).filter(qa => qa.score != null);

    return (
      submission.total_score_override
      ?? submission.total_score
      ?? (scored.length ? scored.reduce((acc, cur) => acc + parseFloat(cur.score), 0) : null)
    );
  }, [questionAnswers, submission?.total_score, submission?.total_score_override]);
};

const SubmissionScore = ({ classNames = {}, editable = true, labelText, onChange }: SubmissionScoreProps) => {
  const assignment = useGradingStore(state => state.assignment);
  const submission = useGradingStore(state => state.submission);
  const showAnswers = useGradingStore(state => state.showAnswers());

  const score = useSubmissionScore();

  const submissionScoreAssignment = {
    ...assignment,
    maxScore: assignment.max_points.toString(),
  } as any as ISubmissionScoreEditAssignment;

  return (
    <SubmissionScoreEdit
      assignment={submissionScoreAssignment}
      classNames={{
        input: '[&>input]:tw-ml-0',
        label: 'tw-mb-2',
        root: 'tw-flex-col tw-items-start',
        saveStatus: 'tw-text-left tw-text-balance tw-whitespace-normal',
        ...classNames,
      }}
      editable={editable}
      labelText={labelText}
      showAnswers={showAnswers}
      submission={{ ...submission, total_score: score } as any as ISubmission}
      onChange={onChange}
    />
  );
};

export default SubmissionScore;
